import styled from 'styled-components';
import { fontSizeDefinitions_DEPRECATED } from '../../atomicComponents/fonts/fontSizeDefinitionsWithDefaultFont';
import {
  APIComponentAttributes,
  APIFieldAttributes,
  APIOrganizationUser,
  APIReferenceAttributes,
  ArdoqId,
} from '@ardoq/api-types';
import { dateRangeOperations, isDateRangeFieldType } from '@ardoq/date-range';
import {
  Header,
  HeaderAndTableContainer,
  TableRow,
  TableRowContainer,
} from 'tabview/pagesView/commonUIElements';
import { Tags } from 'tabview/pagesView/Tags';
import { FieldLabel } from 'tabview/pagesView/FieldLabel';
import {
  formatFieldContent,
  getFieldValues,
} from 'tabview/pagesView/FieldUtils';
import { colors } from '@ardoq/design-tokens';
import { fieldOps } from 'models/utils/fieldOps';
import { TAGS_FIELD_NAME } from '@ardoq/data-model';
import { FlexBox } from '@ardoq/layout';
import { RedactedFieldDisplay } from '@ardoq/renderers';
import { t, type I18nLocale } from '@ardoq/i18n';
import type { Locale } from '@ardoq/locale';
import type { ComponentTag } from 'tabview/pagesView/types';
import type { AttachmentWithContext } from 'streams/documentArchive/types';

const FieldContent = styled.div`
  ${fontSizeDefinitions_DEPRECATED.MEDIUM}
  color: ${colors.grey950};
  width: 70%;
`;
interface FieldsProps {
  fields: APIFieldAttributes[];
  defaultFields: Pick<APIFieldAttributes, 'name' | '_id' | 'label' | 'type'>[];
  modelId: string;
  attributes: APIComponentAttributes | APIReferenceAttributes | null;
  referenceTypeName: string | null;
  usersById: Record<ArdoqId, APIOrganizationUser>;
  attachmentsById: Record<ArdoqId, AttachmentWithContext>;
  tags: ComponentTag[];
  redactedFieldNames: Set<string>;
  showHeader: boolean;
  hideEmptyFields: boolean;
  isLocalDateFormat?: boolean;
  i18nLocale: I18nLocale;
  locale: Locale;
}
const emptyValues = [null, undefined, ''];

const getConfidentialFieldName = (
  field: Pick<APIFieldAttributes, 'name' | 'type'>
): string => {
  if (isDateRangeFieldType(field.type))
    return dateRangeOperations.toStartDateName(field.name);
  return field.name;
};

const getFieldDescription = (
  field: FieldsProps['defaultFields'][number] | APIFieldAttributes
): string => ('description' in field ? fieldOps.getDescription(field) : '');

const Fields = ({
  modelId,
  attributes,
  referenceTypeName,
  usersById,
  attachmentsById,
  tags,
  defaultFields,
  fields,
  redactedFieldNames,
  showHeader,
  hideEmptyFields,
  isLocalDateFormat,
  i18nLocale,
  locale,
}: FieldsProps) => {
  const tagIds = tags.map(tag => tag.id);
  const fieldValues = getFieldValues(
    attributes,
    [...defaultFields, ...fields],
    tagIds,
    referenceTypeName
  );
  const fieldsToShow = [...defaultFields, ...fields].filter(
    field => !hideEmptyFields || !emptyValues.includes(fieldValues[field.name])
  );

  return fieldsToShow.length ? (
    <HeaderAndTableContainer>
      {showHeader && (
        <Header>
          {t(i18nLocale, 'tabview:pagesView.fields.header', 'Fields')}
        </Header>
      )}
      <>
        {fieldsToShow.map(field => (
          <TableRowContainer
            key={field._id}
            role="group"
            aria-label={field.name === TAGS_FIELD_NAME ? 'Tags' : field.label}
          >
            <TableRow>
              <FieldLabel
                text={field.name === TAGS_FIELD_NAME ? 'Tags' : field.label}
                fieldId={field._id}
                description={getFieldDescription(field)}
              />
              <FieldContent
                aria-label={t(
                  i18nLocale,
                  'tabview:pagesView.fields.fieldValueAriaLabel',
                  'field value'
                )}
              >
                <FlexBox gap="small">
                  <FlexBox flex={1}>
                    {field.name === TAGS_FIELD_NAME ? (
                      <Tags tags={tags} />
                    ) : redactedFieldNames.has(
                        getConfidentialFieldName(field)
                      ) ? (
                      <RedactedFieldDisplay i18nLocale={i18nLocale} />
                    ) : (
                      formatFieldContent({
                        value: fieldValues[field.name],
                        modelId,
                        attributes,
                        field,
                        isLocalDateFormat,
                        usersById,
                        attachmentsById,
                        i18nLocale,
                        locale,
                      })
                    )}
                  </FlexBox>
                </FlexBox>
              </FieldContent>
            </TableRow>
          </TableRowContainer>
        ))}
      </>
    </HeaderAndTableContainer>
  ) : null;
};
export { Fields };
